import React from "react";

import { useIntl } from "react-intl";

import { BannerAlert } from "@/components/wpl/banner-alert/src/banner-alert.tsx";
import { DemoAddOn, QuestionsCta } from "@/components/wpl/pricing/src";
import { PricingFeatureTable } from "@/components/wpl/pricing/src/features-table";
import { usePricingContentContext } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { PricingFaq } from "@/components/wpl/pricing/src/pricing-faq/pricing-faq";
import { QuotationsAdornment } from "@/components/wpl/pricing/src/pricing-faq/quotations.tsx";
import {
    PricingPageLayout,
    PageHeading
} from "@/components/wpl/pricing/src/pricing-page-layout";
import { useLocalizedHref } from "@/libraries/web-framework/index.ts";
import { BannerProps } from "@/patterns/adg4/banner-alert/banner-alert.tsx";
import { CallToAction } from "@/utils/pricing-queries/get-pricing-content-gql.ts";
import { ParentScrollTracking } from "@/utils/scroll-tracking";

import { SoftwareGuardPricingPageProps } from "../../../../../../pages/software/guard/pricing/software-guard-pricing-page.tsx";
import { GuardNav } from "../../../src/sections/guard-navbar.tsx";

import GuardPriceFinder from "./sections/guard-price-finder.tsx";

export const SoftwareGuardPricingPageTemplate: React.FC<
    SoftwareGuardPricingPageProps
    // eslint-disable-next-line complexity
> = (props: SoftwareGuardPricingPageProps) => {
    const content = usePricingContentContext();
    const questionsCtaContent = content
        ?.callToActionContainer?.[0] as CallToAction;
    const addonCtaContent = content?.callToActionContainer?.[1] as CallToAction;
    const intl = useIntl();
    const bannerProps: BannerProps = {
        identifier: "GuardPricingPage.banner-alert.premium-edition",
        productKey: "atlassianGuard",
        text: intl.formatMessage({
            id: "GuardPricingPage.banner-alert.text",
            defaultMessage: "Atlassian Guard Premium is generally available!"
        }),
        linkText: intl.formatMessage({
            id: "GuardPricingPage.banner-alert.link-text",
            defaultMessage: "Read the blog."
        }),
        linkUrl: useLocalizedHref("/blog/announcements/guard-premium"),
        dataEventProps: {
            "data-product-key": "atlassianGuard",
            "data-event-component": "linkButton",
            "data-event-container": "announcementBanner",
            "data-event": "clicked",
            "data-label": "Read the blog",
            "data-label-english": "Read the blog"
        }
    };
    return (
        <>
            <PricingPageLayout
                nav={<GuardNav />}
                banner={<BannerAlert {...bannerProps} />}
                showFallback={content?.hasError}
            >
                <ParentScrollTracking
                    scrollTrackingEnabled={true}
                    pageId="guard-pricing-page"
                >
                    <PageHeading>{content?.headline}</PageHeading>
                    <GuardPriceFinder
                        pricing={props.pricing}
                        countryCode={props.countryCode}
                    />
                    <PricingFeatureTable
                        title={content?.compareFeaturesTitle ?? undefined}
                        comparePlans={content?.comparePlans ?? undefined}
                        compareFeatures={content?.compareFeatures ?? undefined}
                    />
                    <DemoAddOn
                        headingText={addonCtaContent?.title}
                        bodyText={addonCtaContent?.valueProposition}
                        linkText={addonCtaContent?.text}
                        ctaHref={addonCtaContent?.url ?? ""}
                    />
                    <QuotationsAdornment>
                        <ParentScrollTracking>
                            <PricingFaq
                                header={
                                    content?.getMoreDetailsTitle ?? undefined
                                }
                                planDetails={
                                    content?.additionalDetails ?? undefined
                                }
                            />
                        </ParentScrollTracking>
                    </QuotationsAdornment>
                    <QuestionsCta
                        product={"Guard"}
                        ctaHref={questionsCtaContent?.url ?? undefined}
                        questionsHeader={
                            questionsCtaContent?.title ?? undefined
                        }
                        priceAndLicensingLinkText={
                            questionsCtaContent?.text ?? undefined
                        }
                        questionsBody={
                            questionsCtaContent?.valueProposition ?? undefined
                        }
                        imageHref={questionsCtaContent?.asset?.url}
                    />
                </ParentScrollTracking>
            </PricingPageLayout>
        </>
    );
};
